 
import HeaderSearch from "@components/C01.2_HeaderSearch/C01.2_HeaderSearch";
import Link from "@components/Link/Link";
import { FC, useCallback, useContext, useMemo } from "react";
import { UserContext } from "../../context/user";
import HamburgerSvg from "../../public/image/svg/menu.svg";
import ProfileSvg from "../../public/image/svg/profile.svg";
import SearchSvg from "../../public/image/svg/search.svg";
import styles from "./C01_SiteHeader.module.scss";

import { MobileLevelSection } from "@components/C01_SiteHeader/mobileMenu/MobileLevelSection";
import { handleItemClick } from "@components/C01_SiteHeader/mobileMenu/utils";
import { NextContentfulImage } from "@components/Image";
import { TypeSiteHeaderFields } from "@customTypes/contentful";
import { eraseUserCookies } from "@utilities/cookies";
import { COVEO_IGNORE_CLASS } from "@utilities/coveoConst";
import { HEADER_ANCHOR } from "@utilities/e2e/e2eAnchorsList";
import { getE2EAnchorAttribute } from "@utilities/e2e/getE2EAnchor";
import { navigationGAClick } from "@utilities/navigationGAClick";
import { useDevice } from "@utilities/react/get-device/get-device";
import clsx from "clsx";
import { useNavigationStateContext } from "../../context/navigationContext";
import MainNav from "./MainNav";

export type Props = {
  displayBreadcrumbs?: boolean;
  content: TypeSiteHeaderFields & {
    displayNav?: boolean;
  };
  siteRef: React.RefObject<HTMLElement | null>;
};

export const SiteHeader: FC<Props> = ({ content, siteRef }) => {
  const {
    logo,
    loginText,
    logoutText,
    loginUrl,
    logoutUrl,
    megaNav,
    secondaryLinks,
    skipToContentText,
    areaOfInterestLinks,
    areaOfInterestText,
    searchHelpText,
    searchPlaceholderText,
    searchResultsUrl,
    displayNav,
  } = content;
  const { user } = useContext(UserContext);
  const { showSearch, setShowSearch, mobileMenuHistory, setMobileMenuHistory } =
    useNavigationStateContext();

  const searchContent = useMemo(
    () => ({
      areaOfInterestLinks,
      areaOfInterestText,
      searchHelpText,
      searchPlaceholderText,
      searchResultsUrl,
    }),
    [
      areaOfInterestLinks,
      areaOfInterestText,
      searchHelpText,
      searchPlaceholderText,
      searchResultsUrl,
    ]
  );

  const hideSearch = useCallback(() => {
    setShowSearch(false);
  }, [setShowSearch]);

  const device = useDevice();
  const isMobile = device === "mobile";
  const logoImage = useMemo(
    () => (
      <NextContentfulImage
        src={logo?.src}
        alt={logo?.description || "London Business School logo"}
        fill
      />
    ),
    [logo?.src, logo?.description]
  );
  const isMenuOpen = isMobile && mobileMenuHistory?.length > 0;

  return (
    <>
      {!!mobileMenuHistory?.length && isMobile && (
        <button
          onClick={(e) => {
            setMobileMenuHistory([]);
            navigationGAClick(e);
          }}
          aria-label="Close menu"
          className={clsx(styles["bg-mobile-menu"])}
          {...getE2EAnchorAttribute(HEADER_ANCHOR, "close-menu-btn")}
        />
      )}
      <a
        href="#skipnav"
        className={styles.skip}
        {...getE2EAnchorAttribute(HEADER_ANCHOR, "skip-to-content-btn")}
      >
        {skipToContentText}
      </a>
      <header className={clsx(styles["site-header"], COVEO_IGNORE_CLASS)}>
        <div className={clsx("wrapper", styles.wrapper)}>
          {displayNav && (
            <>
              <nav className={styles.top} aria-label="Secondary navigation">
                <Link
                  href="/"
                  onClick={navigationGAClick}
                  className={clsx(
                    styles["mobile-home-link"],
                    "hover:no-underline md:hidden"
                  )}
                  aria-label="Home link"
                  {...getE2EAnchorAttribute(HEADER_ANCHOR, "home-link_mobile")}
                >
                  {logoImage}
                </Link>
                <ul className={styles["secondary-nav"]}>
                  {secondaryLinks?.map((item, i) => {
                    if (user && item?.url === "/create-a-profile") return;

                    if (user && item?.url === "/portal") {
                      return (
                        <li key={`${item}${i}`}>
                          <a
                            href="https://portal.london.edu/?forwardAzureAD=true"
                            onClick={navigationGAClick}
                          >
                            {item?.linkText}
                          </a>
                        </li>
                      );
                    }

                    const url = item?.url || "#";
                    return (
                      <li key={`${item?.linkText}${i}`}>
                        <Link href={url} onClick={navigationGAClick}>
                          {item?.linkText}
                        </Link>
                      </li>
                    );
                  })}
                  <li>
                    <a
                      {...getE2EAnchorAttribute(
                        HEADER_ANCHOR,
                        user ? "logout-btn" : "login-btn"
                      )}
                      href={user ? logoutUrl : loginUrl}
                      onClick={() => {
                        if (user) {
                          eraseUserCookies();
                        }
                      }}
                    >
                      {user ? logoutText : loginText}
                    </a>
                  </li>
                  {user && (
                    <li className={styles.icon}>
                      <Link
                        href="/my-profile"
                        aria-label="My Account"
                        {...getE2EAnchorAttribute(
                          HEADER_ANCHOR,
                          "my-account-btn"
                        )}
                      >
                        <ProfileSvg />
                      </Link>
                    </li>
                  )}
                  <li className={styles.icon}>
                    <button
                      className={styles["search-btn"]}
                      aria-label="Search"
                      aria-haspopup="true"
                      onClick={(e) => {
                        e.preventDefault();
                        setShowSearch(!showSearch);
                      }}
                      {...getE2EAnchorAttribute(HEADER_ANCHOR, "search-btn")}
                    >
                      <SearchSvg />
                    </button>
                  </li>

                  {/* NEW mobile nav */}
                  <li className={`${styles.icon} ${styles["mobile-btn"]}`}>
                    <button
                      className={styles["burger-btn"]}
                      aria-label={isMenuOpen ? "Close menu" : "Open menu"}
                      aria-expanded={isMobile ? isMenuOpen : undefined}
                      aria-controls={isMobile ? "mobile-menu" : undefined}
                      {...getE2EAnchorAttribute(HEADER_ANCHOR, "burger-btn")}
                      onClick={(e) => {
                        e.preventDefault();
                        handleItemClick(
                          mobileMenuHistory,
                          setMobileMenuHistory,
                          // @ts-expect-error types are too hard here
                          megaNav,
                          undefined,
                          undefined,
                          undefined
                        );
                      }}
                    >
                      <span role="presentation" className="icon-hamburger">
                        <HamburgerSvg />
                      </span>
                    </button>
                  </li>
                </ul>
              </nav>
            </>
          )}
          <div className={styles.main}>
            <div className={clsx(styles.logo, "hidden md:block")}>
              <Link
                href="/"
                onClick={navigationGAClick}
                {...getE2EAnchorAttribute(HEADER_ANCHOR, "home-link")}
                aria-label="Home link"
              >
                <div className={clsx("image-hover", styles.image)}>
                  {logoImage}
                </div>
              </Link>
            </div>
            {displayNav && !isMobile && <MainNav megaNav={megaNav} />}
          </div>
          {isMobile && (
            <div id="mobile-menu">
              {mobileMenuHistory?.map((level, index) => (
                <MobileLevelSection
                  key={index}
                  // @ts-expect-error types mistmatch
                  levelData={level}
                  isFirstLevel={index === 0}
                  secondaryLinks={index === 0 ? secondaryLinks : undefined}
                  user={user}
                  siteRef={siteRef}
                />
              ))}
            </div>
          )}
        </div>
        <HeaderSearch
          content={searchContent}
          show={showSearch}
          close={hideSearch}
        />
      </header>
      <div id="skipnav" tabIndex={-1} />
    </>
  );
};

export default SiteHeader;
